import { useMemo } from 'react';
import type { DrillDownRow, TimeGranularity, ChartData } from '../types';
import { CHART_CONSTANTS } from '../constants';
import { getAutoGranularity, getPeriodKey, formatPeriodLabel } from '../utils';

export interface UseChartDataOptions {
  data: DrillDownRow[];
  comparisonData?: DrillDownRow[] | null;
  startDate: Date;
  endDate: Date;
  comparisonStartDate?: Date;
  explicitGranularity?: TimeGranularity;
  selectedDimensions?: string[];
}

/**
 * Processes raw drill-down data into chart-ready aggregated data.
 * Handles period generation, data aggregation, and metric series computation.
 */
export function useChartData({
  data,
  comparisonData,
  startDate,
  endDate,
  comparisonStartDate,
  explicitGranularity,
  selectedDimensions = [],
}: UseChartDataOptions): ChartData {
  return useMemo(() => {
    // Use explicit granularity or auto-detect based on date range
    const granularity: TimeGranularity = explicitGranularity || getAutoGranularity(startDate, endDate);

    // Generate periods based on granularity
    const periodKeys = new Set<string>();
    const current = new Date(startDate);

    // Safety: limit to max periods to prevent infinite loops
    let safety = 0;
    const MAX_PERIODS = CHART_CONSTANTS.MAX_PERIODS;

    // For accumulated, just one period
    if (granularity === 'accumulated') {
      periodKeys.add('total');
    } else {
      while (current <= endDate && safety < MAX_PERIODS) {
        safety++;
        const key = getPeriodKey(current, granularity);
        periodKeys.add(key);

        // Advance to next period based on granularity
        switch (granularity) {
          case 'yearly':
            current.setFullYear(current.getFullYear() + 1);
            break;
          case 'quarterly':
            current.setMonth(current.getMonth() + 3);
            break;
          case 'monthly':
            current.setMonth(current.getMonth() + 1);
            break;
          case 'weekly':
            current.setDate(current.getDate() + 7);
            break;
          case 'daily':
          default:
            current.setDate(current.getDate() + 1);
            break;
        }
      }
    }

    // Sort and create period objects
    const periods = Array.from(periodKeys)
      .sort()
      .map(key => ({
        key,
        label: formatPeriodLabel(key, granularity),
      }));

    // Aggregate current data into periods
    const currentTotals = new Map<string, number>();
    data.forEach((row) => {
      if (!row.Data) return;
      const date = new Date(row.Data);
      if (isNaN(date.getTime())) return;
      const key = getPeriodKey(date, granularity);
      // Only include if key is in our period range
      if (periodKeys.has(key)) {
        currentTotals.set(key, (currentTotals.get(key) || 0) + (Number(row.Valor) || 0));
      }
    });

    // Aggregate comparison data with time-shift alignment
    const comparisonTotals = new Map<string, number>();
    if (comparisonData && comparisonStartDate) {
      // Calculate offset to align comparison period with current period
      const offsetMs = startDate.getTime() - comparisonStartDate.getTime();

      comparisonData.forEach((row) => {
        if (!row.Data) return;
        const originalDate = new Date(row.Data);
        if (isNaN(originalDate.getTime())) return;

        // Shift the date forward to align with current period
        const alignedDate = new Date(originalDate.getTime() + offsetMs);
        const key = getPeriodKey(alignedDate, granularity);

        // Only include if key is in our period range
        if (periodKeys.has(key)) {
          comparisonTotals.set(key, (comparisonTotals.get(key) || 0) + (Number(row.Valor) || 0));
        }
      });
    }

    // Count of records per period (for 'count' metric)
    const periodCounts = new Map<string, number>();
    data.forEach((row) => {
      if (!row.Data) return;
      const date = new Date(row.Data);
      if (isNaN(date.getTime())) return;
      const periodKey = getPeriodKey(date, granularity);
      if (periodKeys.has(periodKey)) {
        periodCounts.set(periodKey, (periodCounts.get(periodKey) || 0) + 1);
      }
    });

    // Calculate metrics per period for each selected dimension
    const metricSeries: Map<string, Map<string, number>> = new Map();

    if (selectedDimensions.length > 0) {
      selectedDimensions.forEach((dimKey) => {
        if (dimKey === 'total') {
          // 'total' uses currentTotals (already calculated above)
          metricSeries.set('total', currentTotals);
        } else if (dimKey === 'count') {
          // 'count' uses record count per period
          metricSeries.set('count', periodCounts);
        } else {
          // Dimension: count unique values per period
          const periodUniques = new Map<string, Set<string>>();

          data.forEach((row) => {
            if (!row.Data) return;
            const dimValue = row[dimKey];
            if (dimValue == null || String(dimValue).trim() === '') return;

            const date = new Date(row.Data);
            if (isNaN(date.getTime())) return;
            const periodKey = getPeriodKey(date, granularity);

            if (periodKeys.has(periodKey)) {
              const existing = periodUniques.get(periodKey) || new Set<string>();
              existing.add(String(dimValue).trim());
              periodUniques.set(periodKey, existing);
            }
          });

          // Convert Sets to counts
          const uniqueCounts = new Map<string, number>();
          periodUniques.forEach((uniqueSet, periodKey) => {
            uniqueCounts.set(periodKey, uniqueSet.size);
          });

          metricSeries.set(dimKey, uniqueCounts);
        }
      });
    }

    return {
      periods,
      currentTotals,
      comparisonTotals,
      periodCounts,
      granularity,
      hasComparison: Boolean(comparisonData && comparisonData.length > 0),
      metricSeries,
      hasMetricSelection: selectedDimensions.length > 0,
    };
  }, [data, comparisonData, startDate, endDate, comparisonStartDate, explicitGranularity, selectedDimensions]);
}
